package co.maytes.standupbot.dashboard.features.setup

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupCheckStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone

@Composable
fun TestMySetupButton(
    variant: ButtonVariant = ButtonVariant.Secondary,
    size: ButtonSize = ButtonSize.Sm,
    idleLabel: String? = null,
) {
    val auth = rememberAuthStore()
    val checkStore = rememberSetupCheckStore()
    val statusStore = rememberSetupStatusStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id
    if (!auth.isAdmin) return
    val state = checkStore.state
    Button(
        label = idleLabel ?: if (state.report == null) "Test my setup" else "Run again",
        variant = variant,
        size = size,
        enabled = state.canStart && !workspaceId.isNullOrBlank(),
        loading = state.running,
        onClick = {
            val ws = workspaceId ?: return@Button
            scope.launch { if (checkStore.run(ws)) statusStore.refresh(ws) }
        },
    )
}

@Composable
fun SetupCheckReportView() {
    val state = rememberSetupCheckStore().state
    val ctx = rememberPageContext()
    val report = state.report
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .gap(Spacing.sm)
                .thenIf(report != null, Modifier.classNames(HomeClasses.PANEL, HomeClasses.PAD)),
    ) {
        state.error?.let { message ->
            SpanText(
                text = message,
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.status(StatusHue.Danger).solid),
            )
        }
        if (report == null) {
            if (state.running) SkeletonList(rows = 3)
            return@Column
        }
        val model = remember(report) { SetupCheckModel.from(report, TimeZone.currentSystemDefault()) }
        SpanText(
            text = "${model.ranAtLabel} · ${model.summary}",
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
        )
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
            model.rows.forEach { row -> CheckRowView(row, onAction = { path -> ctx.router.navigateTo(path) }) }
        }
    }
}

@Composable
private fun CheckRowView(
    row: CheckRow,
    onAction: (String) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.sm)
            .padding(topBottom = Spacing.xxs)
            .styleModifier { property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CheckGlyphMark(row.glyph)
        Column(modifier = Modifier.styleModifier { property("flex", "1 1 60%"); property("min-width", "0") }) {
            Row(modifier = Modifier.gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") }) {
                SpanText(
                    text = row.title,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(500)
                        .color(if (row.isFailure) Palette.status(StatusHue.Danger).solid else Palette.textPrimary),
                )
                row.subject?.let { subject ->
                    SpanText(
                        text = subject,
                        modifier = Modifier.fontSize(Typography.body.size).color(Palette.textSecondary),
                    )
                }
            }
            if (row.detail.isNotBlank()) {
                SpanText(
                    text = row.detail,
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
                )
            }
        }
        row.action?.let { action ->
            Button(
                label = action.label,
                variant = if (row.isFailure) ButtonVariant.Primary else ButtonVariant.Secondary,
                size = ButtonSize.Sm,
                onClick = { onAction(action.path) },
            )
        }
    }
}

private const val CHECK_GLYPH_SIZE = 16

@Composable
private fun CheckGlyphMark(glyph: CheckGlyph) {
    Box(
        modifier = Modifier
            .width(Spacing.md)
            .height(Spacing.md)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            },
    ) {
        when (glyph) {
            CheckGlyph.Pass -> LucideCheck(size = CHECK_GLYPH_SIZE, strokeColor = Palette.status(StatusHue.Success).solid.cssRgb())
            CheckGlyph.Warn -> LucideAlertTriangle(size = CHECK_GLYPH_SIZE, strokeColor = Palette.status(StatusHue.Warning).solid.cssRgb())
            CheckGlyph.Fail -> LucideX(size = CHECK_GLYPH_SIZE, strokeColor = Palette.status(StatusHue.Danger).solid.cssRgb())
            CheckGlyph.Skipped ->
                Box(
                    modifier = Modifier
                        .width(Spacing.sm)
                        .height(Spacing.sm)
                        .styleModifier {
                            property("border", "1.5px solid ${Palette.textTertiary.cssRgb()}")
                            property("border-radius", "50%")
                        },
                )
        }
    }
}
